import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import './InfoTooltip.css';

type TooltipVariant = 'white' | 'black';

interface InfoTooltipProps {
  text: string;
  variant?: TooltipVariant;
  className?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  variant = 'white',
  className = '',
}) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (show && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      let left = rect.left + rect.width / 2;
      let top = rect.bottom + 8;
      if (window.innerWidth < 500) {
        // Limiter le centrage sans débordement
        const bubbleWidth = Math.min(300, window.innerWidth * 0.9); // même valeur que max-width
        left = Math.min(
          Math.max(left, bubbleWidth / 2 + 8), // 8px de marge à gauche
          window.innerWidth - bubbleWidth / 2 - 8 // 8px de marge à droite
        );
      }
      setPosition({ top, left });
    }
  }, [show]);

  const renderTooltip = () => {
    if (!show) return null;

    return createPortal(
      <div
        className="info-tooltip-bubble"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          transform: 'translateX(-50%)',
        }}
      >
        {text}
        <div className="info-tooltip-arrow" />
      </div>,
      document.body
    );
  };

  return (
    <>
      <span
        ref={containerRef}
        className={clsx(
          'info-tooltip-trigger',
          {
            'trigger-white': variant === 'white',
            'trigger-black': variant === 'black',
          },
          className
        )}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <Info size={24} />
      </span>
      {renderTooltip()}
    </>
  );
};